'use client';

import clsx from 'clsx';
import type React from 'react';
import { createContext, useContext, useState } from 'react';

type CSSWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};
import { useThemeContext } from '../../../utils/providers/theme-context';
import { Link } from './link';

const TableContext = createContext<{
  bleed: boolean;
  dense: boolean;
  grid: boolean;
  striped: boolean;
}>({
  bleed: false,
  dense: true,
  grid: false,
  striped: true
});

export function Table({
  bleed = false,
  dense = true,
  grid = false,
  striped = true,
  className,
  children,
  ...props
}: {
  bleed?: boolean;
  dense?: boolean;
  grid?: boolean;
  striped?: boolean;
} & React.ComponentPropsWithoutRef<'div'>) {
  const { theme, getColour } = useThemeContext();

  const text = getColour('normalText');

  const borderColor =
    theme === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.08)'; // slightly darker for light
  const headText =
    theme === 'dark' ? 'rgba(255,255,255,0.75)' : 'rgba(17,24,39,0.62)';

  const stripeEven =
    theme === 'dark' ? 'rgba(200,205,210,0.07)' : 'rgba(120,130,140,0.08)';
  const hoverLinkRow =
    theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';
  const hoverNonStriped =
    theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  // Explicit base background so the sticky actions column can reference
  // var(--tbl-bg) and match the non-sticky cells exactly. Without this the
  // sticky cell needs a hardcoded color that may differ from the parent surface.
  const baseBg = getColour('secondLayer');
  // Pre-computed opaque result of stripe-even composited over baseBg.
  // The sticky <td> cannot use the semi-transparent stripe rgba directly
  // because the <tr> CSS class already applies that overlay — combining them
  // would double-darken the cell. Use these solid values instead.
  const stripeSolid = getColour('firstLayer');

  return (
    <TableContext.Provider
      value={
        { bleed, dense, grid, striped } as React.ContextType<
          typeof TableContext
        >
      }
    >
      <div className="flow-root h-full">
        <div
          {...props}
          className={clsx(className, 'overflow-x-auto h-full')}
          style={
            {
              color: text,
              backgroundColor: baseBg,
              '--tbl-bg': baseBg,
              '--tbl-stripe-solid': stripeSolid,
              '--tbl-border': borderColor,
              '--tbl-head': headText,
              '--tbl-stripe-even': stripeEven,
              '--tbl-hover-link': hoverLinkRow,
              '--tbl-hover-ns': hoverNonStriped
            } as CSSWithVars
          }
        >
          <div
            className={clsx('inline-block min-w-full min-h-full align-middle')}
          >
            <table
              className="min-w-full min-h-full text-left text-sm/6"
              style={{ color: text }}
            >
              {children}
            </table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

export function TableHead({
  className,
  style,
  ...props
}: React.ComponentPropsWithoutRef<'thead'>) {
  return (
    <thead
      {...props}
      className={clsx(className)}
      style={{ color: 'var(--tbl-head)', ...style }}
    />
  );
}

export function TableBody(props: React.ComponentPropsWithoutRef<'tbody'>) {
  return <tbody {...props} />;
}

const TableRowContext = createContext<{
  href?: string;
  target?: string;
  title?: string;
}>({
  href: undefined,
  target: undefined,
  title: undefined
});

export function TableRow({
  href,
  target,
  title,
  className,
  ...props
}: {
  href?: string;
  target?: string;
  title?: string;
} & React.ComponentPropsWithoutRef<'tr'>) {
  const { striped } = useContext(TableContext);

  return (
    <TableRowContext.Provider
      value={
        { href, target, title } as React.ContextType<typeof TableRowContext>
      }
    >
      <tr
        {...props}
        title={title}
        className={clsx(
          className,
          striped && 'even:[background:var(--tbl-stripe-even)]',
          href && striped && 'hover:[background:var(--tbl-hover-link)]',
          href && !striped && 'hover:[background:var(--tbl-hover-ns)]',
          href &&
            'has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-blue-500'
        )}
      />
    </TableRowContext.Provider>
  );
}

export function TableHeader({
  className,
  style,
  ...props
}: React.ComponentPropsWithoutRef<'th'>) {
  const { bleed, grid } = useContext(TableContext);

  return (
    <th
      {...props}
      className={clsx(
        className,
        'px-4 py-4 font-medium uppercase first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2))',
        !bleed && 'sm:first:pl-1 sm:last:pr-1'
      )}
      style={{
        borderBottom: '1px solid var(--tbl-border)',
        ...(grid ? { borderLeft: '1px solid var(--tbl-border)' } : {}),
        ...style
      }}
    />
  );
}

export function TableCell({
  className,
  children,
  style,
  ...props
}: React.ComponentPropsWithoutRef<'td'>) {
  const { bleed, dense, grid, striped } = useContext(TableContext);
  const { href, target, title } = useContext(TableRowContext);
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null);

  return (
    <td
      ref={href ? setCellRef : undefined}
      {...props}
      className={clsx(
        className,
        'relative px-4 first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2))',
        dense ? 'py-2.5' : 'py-4',
        !bleed && 'sm:first:pl-1 sm:last:pr-1'
      )}
      style={{
        ...(striped ? {} : { borderBottom: '1px solid var(--tbl-border)' }),
        ...(grid ? { borderLeft: '1px solid var(--tbl-border)' } : {}),
        ...style
      }}
    >
      {href && (
        <Link
          data-row-link
          href={href}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-hidden"
        />
      )}
      {children}
    </td>
  );
}
